import {Injectable}                from '@angular/core';
import {GetRoundNumericService}    from './get-round-numeric.service';
import {APP_CONFIGURATION}         from '../../ap-core/config';
import {ApElementOxydTypeEnum}     from '../../ap-interface';
import {SettingsStore}             from '../../stores/base-data/settings.store';
import {ApTranslationService}      from './ap-translation.service';
import {GetElementService}         from './ap-get-element.service';
import {FertilizerStore}           from '../../stores/base-data/fertilizer.store';
import {GetApplicationModeService} from './ap-get-applicationmode.service';
import {SampleMethodService}       from '../../services/data/sample.method.service';
import {IGridStatus}               from '../../ap-interface/interfaces/ap-grid-status.interface';
import {ApColumnState}             from '../../ap-interface/enums/ap-column-state.enum';
import {CropRotationYield}         from '../../stores/farm/crop.rotation.store';
import {ApElementType}             from '../../ap-interface/enums/ap-elements-type.enum';
import {CropTypeStore}             from '../../stores/base-data/crop.types.store';
import IApplicationMode = Data.Common.IApplicationMode;

@Injectable({providedIn: 'root'})
export class ApNutrientService {
  constructor(private settingsStore: SettingsStore,
              private fertilizerStore: FertilizerStore,
              private elementService: GetElementService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private sampleMethodService: SampleMethodService,
              private cropTypeStore: CropTypeStore,
              private applicationModeService: GetApplicationModeService) {
  }

  public isOxidFarm(): boolean {
    return this.settingsStore.FirstSetting.UnitElementOxyd === 2;
  }

  public formatClassificationTooltip(value: number, hitClassE: boolean, method: string): string {
    if (value === undefined || value === null) {
      console.warn(`invalid value for class value in formatClassificationTooltip ${value}`);
      return '-';
    }

    const rounded = this.roundNumericService.round(value, APP_CONFIGURATION.SpecialDecimalPlaces.Distributions);
    let buffer = '';
    if (hitClassE) {
      buffer += '> ';
    }
    buffer += `${rounded}`;
    if (method) {
      buffer += ` ${this.sampleMethodService.getUnitWithMethod(method, false)}`;
    } else {
      console.warn(`missing method in formatClassificationTooltip`);
    }
    return buffer;
  }

  public convertElementOxid(value: number, element: string, method: string): number {
    return this.elementService.convertElementOxid(value, element, method);
  }

  /**
   * Convert from Element to Product
   * @param productId of the fertiliser
   * @param element Lead nutrient
   * @param value the element amount
   */
  public convertNutrientToGoods(productId: number, element: number, value: number): number {
    if (productId === 0 || productId === null) {
      return value;
    }
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return value;
    }
    let content = 1;
    let result = value;
    switch (element) {
      case 1:
        content = fertilizer.ContentP !== 0 ? fertilizer.ContentP : 1;
        break;
      case 2:
        content = fertilizer.ContentK !== 0 ? fertilizer.ContentK : 1;
        break;
      case 3:
        content = fertilizer.ContentMg !== 0 ? fertilizer.ContentMg : 1;
        break;
      case 4:
        content = fertilizer.ContentCaO !== 0 ? fertilizer.ContentCaO : 1;
        break;
      case 5:
        content = fertilizer.ContentN !== 0 ? fertilizer.ContentN : 1;
        break;
    }

    if (fertilizer.IsLiquid) {
      const density = fertilizer.Density !== null ? fertilizer.Density : 1;
      result = result / density;
    }

    if (fertilizer.IsOrganic) {
      // Organic
      return result / content;
    } else {
      // Inorganic
      return result / content * 100;
    }
  }

  /**
   * Defines the lead nutrient
   * → Element with the highest content
   * 1 -P
   * 2 - K
   * 3 - Mg
   * 4 - CaO
   * @param productId the id of the fertiliser
   */
  public getFertilizerLeadingElement(productId: number): number {
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }

    const elementContents = [fertilizer.ContentP, fertilizer.ContentK, fertilizer.ContentMg, fertilizer.ContentCaO];
    const absElementContents = elementContents.map(value => Math.abs(value));
    const maxElementContentsIndex = elementContents.indexOf(Math.max(...elementContents));
    return elementContents[maxElementContentsIndex] > 0 ? maxElementContentsIndex + 1 : absElementContents.indexOf(Math.max(...absElementContents)) + 1;
  }

  public getFertilizerStatus(applicationMode: IApplicationMode, isBooking: boolean): IGridStatus {
    if (isBooking) {
      return {
        state: ApColumnState.Grey,
        class: 'ap-status-grey',
        description: ''
      };
    }
    return this.applicationModeService.GetApplicationModeStatus(applicationMode);
  }

  public getFertilizerStatusDescription(applicationMode: IApplicationMode, isBooking: boolean): string {
    let description = this.applicationModeService.GetApplicationModeTranslation(applicationMode);
    if (isBooking) {
      description = description + ' ' + `(${this.translationService.translate('Nutrients__Booked')})`;
    }
    return description;
  }

  /**
   * Convert from product to nutrient
   * @param productId of the fertiliser
   * @param element lead nutrient
   * @param value of the product
   * @param withOxid with oxid conversion
   */
  public convertGoodsToNutrient(productId: number, element: number, value: number, withOxid: boolean = true): number {
    if (productId === 0 || productId === null) {
      return value;
    }
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return value;
    }
    let content = 1;
    let result = value;
    let unit: ApElementOxydTypeEnum;
    switch (element) {
      case 1:
        content = fertilizer.ContentP !== 0 ? fertilizer.ContentP : 1;
        unit = ApElementOxydTypeEnum.P;
        break;
      case 2:
        content = fertilizer.ContentK !== 0 ? fertilizer.ContentK : 1;
        unit = ApElementOxydTypeEnum.K;
        break;
      case 3:
        content = fertilizer.ContentMg !== 0 ? fertilizer.ContentMg : 1;
        unit = ApElementOxydTypeEnum.Mg;
        break;
      case 4:
        content = fertilizer.ContentCaO !== 0 ? fertilizer.ContentCaO : 1;
        unit = undefined;
        break;
      case 5:
        content = fertilizer.ContentN !== 0 ? fertilizer.ContentN : 1;
        unit = undefined;
        break;
    }

    if (fertilizer.IsLiquid) {
      const density = fertilizer.Density !== null ? fertilizer.Density : 1;
      result = result * density;
    }

    if (fertilizer.IsOrganic) {
      // Organic
      result = result * content;
    } else {
      // Inorganic/ mineral
      result = result * content / 100;
    }
    // convert from element to oxis if farm is oxid.
    if (withOxid) {
      return this.elementService.CalculateElementOxidValueByGivenElementValue(result, unit);
    }

    return result;
  }

  /**
   * Calculates crop's removal (P/K/Mg) for a specific crop and year
   * @param cropRotationYield crop for a year
   */
  public getRemovalFromCrops(cropRotationYield: CropRotationYield)
    : { RemovalP: number, RemovalK: number, RemovalMg: number } {
    let expectedYield = 0;
    let removalP = 0;
    let removalK = 0;
    let removalMg = 0;
    const mainCrop = this.cropTypeStore.getDefaultCroptype(cropRotationYield.MainCrop);
    if (cropRotationYield.MainCrop && mainCrop) {
      removalP += this._calculateCropExtraction(cropRotationYield.MainCropStraw, mainCrop.MainwdP, mainCrop.SecwdP, mainCrop.MsRatio);
      removalK += this._calculateCropExtraction(cropRotationYield.MainCropStraw, mainCrop.MainwdK, mainCrop.SecwdK, mainCrop.MsRatio);
      removalMg += this._calculateCropExtraction(cropRotationYield.MainCropStraw, mainCrop.MainwdMg, mainCrop.SecwdMg, mainCrop.MsRatio);
      expectedYield += cropRotationYield.MainCropYield;
    }
    const secondCrop = this.cropTypeStore.getDefaultCroptype(cropRotationYield.SecondCrop);
    if (cropRotationYield.SecondCrop && secondCrop) {
      removalP += secondCrop.MainwdP;
      removalK += secondCrop.MainwdK;
      removalMg += secondCrop.MainwdMg;
      expectedYield += cropRotationYield.SecondCropYield;
    }
    return {
      RemovalP: this.roundNumericService.roundAsNumber(
        this.elementService.CalculateElementOxidValueByElementType(removalP * expectedYield, ApElementType.P)
      ),
      RemovalK: this.roundNumericService.roundAsNumber(
        this.elementService.CalculateElementOxidValueByElementType(removalK * expectedYield, ApElementType.K)
      ),
      RemovalMg: this.roundNumericService.roundAsNumber(
        this.elementService.CalculateElementOxidValueByElementType(removalMg * expectedYield, ApElementType.Mg)
      )
    };
  }

  private _calculateCropExtraction(isStrawRemoved: boolean, mainWd: number, secondWd: number, msRatio: number): number {
    return isStrawRemoved ? mainWd + (secondWd * msRatio) : mainWd;
  }
}
